import { oc } from '@orpc/contract';
import { z } from 'zod';

/**
 * Labor Core 行政申請確認（タグ別 Step 一覧）。
 * `@standandforce/api-contract` に submissionError が入るまでローカル定義。
 * Backend: list-egov-application-steps-by-tag.api-contract.ts
 */

const egovApplicationConfirmationListStatusValues = [
  'notApplied',
  'failed',
  'withdrawing',
  'withdrawn',
  'returned',
  'correctionPending',
  'completed',
  'inProgress',
] as const;

const governmentApplicationStepClassificationCodeValues = [
  'SOCIAL_INSURANCE',
  'LABOR_INSURANCE',
  'UNKNOWN',
] as const;

const listOrderFields = z.enum([
  'target',
  'classification',
  'stepName',
  'status',
  'arriveId',
  'applicationSubmittedAt',
  'dueDate',
  'lastSyncedAt',
]);

export const EgovApplicationConfirmationListStatusSchema = z.enum(
  egovApplicationConfirmationListStatusValues,
);

export const GovernmentApplicationStepClassificationCodeSchema = z.enum(
  governmentApplicationStepClassificationCodeValues,
);

export const OperationEngineTargetSchema = z.object({
  targetType: z.enum(['Person', 'Device', 'AppIntegration', 'Contract']),
  targetId: z.string(),
  targetName: z.string(),
  targetAvatarUrl: z.string().optional(),
});

export const EgovOfficialDocumentSchema = z.object({
  originalName: z.string(),
  s3Url: z.string(),
});

export const EgovSubmissionErrorReportListItemSchema = z.object({
  formName: z.string().nullable(),
  attachedFileName: z.string().nullable(),
  fileName: z.string().nullable(),
  item: z.string().nullable(),
  content: z.string().nullable(),
});

export const sixLanguageDisplayMessagesSchema = z.object({
  en: z.string(),
  ja: z.string(),
  id: z.string(),
  vn: z.string(),
  th: z.string(),
  zhCN: z.string(),
});

export type EgovSubmissionErrorReportListItem = z.infer<
  typeof EgovSubmissionErrorReportListItemSchema
>;

export type SixLanguageDisplayMessages = z.infer<
  typeof sixLanguageDisplayMessagesSchema
>;

export const EgovSubmissionErrorSchema = z.object({
  /** サーバーが userLocale で解決済みの表示文言（フォールバック用） */
  displayMessage: z.string().optional(),
  /** 6 言語の表示文言（一覧・ドロワーで優先） */
  displayMessages: sixLanguageDisplayMessagesSchema.optional(),
  title: z.string().nullable().optional(),
  detail: z.string().nullable().optional(),
  reportList: z.array(EgovSubmissionErrorReportListItemSchema).optional(),
});

export const EgovActiveNoticeSchema = z.object({
  noticeSubId: z.number(),
  noticeTitle: z.string(),
  noticeSentence: z.string().nullable(),
  correctType: z.string().nullable(),
  correctExpiredDate: z.string().nullable(),
  correctCompletedDate: z.string().nullable(),
  issueDate: z.string(),
  noticeDataType: z.string(),
  attachedFiles: z.array(EgovOfficialDocumentSchema),
});

export const ListEgovApplicationStepsByTagRowSchema = z.object({
  operationPackageInstanceId: z.string(),
  operationStepInstanceId: z.string(),
  target: OperationEngineTargetSchema,
  stepName: z.string(),
  classificationCode:
    GovernmentApplicationStepClassificationCodeSchema.nullable(),
  classificationName: z.string().nullable(),
  dueDate: z.string().nullable(),
  status: EgovApplicationConfirmationListStatusSchema,
  applicationSubmittedAt: z.string().nullable(),
  arriveId: z.string().nullable(),
  lastSyncedAt: z.string().nullable(),
  completedAt: z.string().nullable(),
  operationTaskInstanceId: z.string().nullable(),
  officialDocuments: z.array(EgovOfficialDocumentSchema),
  activeNotices: z.array(EgovActiveNoticeSchema),
  submissionError: EgovSubmissionErrorSchema.nullable(),
  /** 申請送信時に選択された GovProfile ID。未申請・未リンク時は null */
  govProfileId: z.string().nullable(),
  /** 補正待ち（サブステータス WaitingForCorrection）。status が failed でも true になり得る */
  isCorrectionPending: z.boolean(),
});

export const ListEgovApplicationStepsByTagInputSchema = z.object({
  tagId: z.string(),
  page: z.coerce.number().int().min(1).optional(),
  pageSize: z.coerce.number().int().min(1).max(2000).default(50),
  orderBy: listOrderFields.optional(),
  orderDirection: z.enum(['asc', 'desc']).default('asc'),
  searchText: z.string().max(500).optional(),
  status: z.array(EgovApplicationConfirmationListStatusSchema).optional(),
});

export const ListEgovApplicationStepsByTagOutputSchema = z.object({
  items: z.array(ListEgovApplicationStepsByTagRowSchema),
  length: z.number(),
  count: z.number(),
  page: z.number(),
  maxPage: z.number(),
});

export const listEgovApplicationStepsByTagContract = oc
  .route({
    method: 'GET',
    path: '/v2/boe/packages/HRF_LABOR_CORE/instance-tags/{tagId}/egov-confirmation-steps',
  })
  .input(ListEgovApplicationStepsByTagInputSchema)
  .output(ListEgovApplicationStepsByTagOutputSchema);

export type ListEgovApplicationStepsByTagInput = z.infer<
  typeof ListEgovApplicationStepsByTagInputSchema
>;
export type ListEgovApplicationStepsByTagOutput = z.infer<
  typeof ListEgovApplicationStepsByTagOutputSchema
>;
export type ListEgovApplicationStepsByTagRowOutput = z.infer<
  typeof ListEgovApplicationStepsByTagRowSchema
>;

export const laborCoreEgovApplicationStepsContract = oc.router({
  listByTag: listEgovApplicationStepsByTagContract,
});
